/**
 * IRS Integration Page
 * IRS E-Filing - OAuth, Sales Tax Returns, Obligations
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Link as LinkIcon,
  Receipt as ReceiptIcon,
  AccountBalance as TaxIcon
} from '@mui/icons-material';
import { hmrcApi, getErrorMessage } from '../api/services';
import type { TaxObligation, SalesTaxReturn } from '../types/api';

const IRSIntegration: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [oauthStatus, setOauthStatus] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [obligations, setObligations] = useState<TaxObligation[]>([]);
  const [selectedObligation, setSelectedObligation] = useState<TaxObligation | null>(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [salesTaxReturn, setVatReturn] = useState<Partial<SalesTaxReturn>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [activeStep, setActiveStep] = useState(0);

  const userId = 'user123'; // TODO: Get from auth context
  const vrn = 'US-EIN-PLACEHOLDER'; // TODO: Get from company settings

  useEffect(() => {
    loadOAuthStatus();
  }, []);

  const loadOAuthStatus = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const status = await hmrcApi.getOAuthStatus();
      setOauthStatus(status);
      
      if (status.is_configured && status.has_valid_token) {
        loadObligations();
      }
    } catch (error) {
      console.error('Failed to load OAuth status:', error);
      setLoadError(getErrorMessage(error));
      // Set a default status so the page still renders
      setOauthStatus({ is_configured: false, has_valid_token: false, environment: 'sandbox' });
    } finally {
      setLoading(false);
    }
  };

  const loadObligations = async () => {
    try {
      const data = await hmrcApi.getTaxObligations(userId, vrn);
      setObligations(data.obligations || []);
    } catch (error) {
      console.error('Failed to load obligations:', error);
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    }
  };

  const handleConnect = async () => {
    try {
      setLoading(true);
      const { authorization_url } = await hmrcApi.initiateAuthorization(userId);
      window.location.href = authorization_url;
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
      setLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    try {
      setLoading(true);
      await hmrcApi.refreshToken(userId);
      setSnackbar({ open: true, message: 'Token refreshed successfully', severity: 'success' });
      loadOAuthStatus();
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSalesTaxReturn = async () => {
    if (!selectedObligation) return;

    try {
      setLoading(true);
      const returnData: SalesTaxReturn = {
        period_key: selectedObligation.period_key,
        vat_due_sales: salesTaxReturn.vat_due_sales || 0,
        vat_due_acquisitions: salesTaxReturn.vat_due_acquisitions || 0,
        total_vat_due: (salesTaxReturn.vat_due_sales || 0) + (salesTaxReturn.vat_due_acquisitions || 0),
        vat_reclaimed_curr_period: salesTaxReturn.vat_reclaimed_curr_period || 0,
        net_vat_due: ((salesTaxReturn.vat_due_sales || 0) + (salesTaxReturn.vat_due_acquisitions || 0)) - (salesTaxReturn.vat_reclaimed_curr_period || 0),
        total_value_sales_ex_vat: salesTaxReturn.total_value_sales_ex_vat || 0,
        total_value_purchases_ex_vat: salesTaxReturn.total_value_purchases_ex_vat || 0,
        total_value_goods_supplied_ex_vat: salesTaxReturn.total_value_goods_supplied_ex_vat || 0,
        total_acquisitions_ex_vat: salesTaxReturn.total_acquisitions_ex_vat || 0,
      };

      await hmrcApi.submitSalesTaxReturn(userId, returnData);
      setSnackbar({ open: true, message: 'Sales Tax return submitted successfully!', severity: 'success' });
      setShowSubmitDialog(false);
      loadObligations();
    } catch (error) {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getObligationStatus = (status: string): { label: string; color: 'warning' | 'success' | 'default'; icon: React.ReactElement | undefined } => {
    switch (status.toLowerCase()) {
      case 'o': return { label: 'Open', color: 'warning' as const, icon: <WarningIcon /> };
      case 'f': return { label: 'Fulfilled', color: 'success' as const, icon: <CheckIcon /> };
      default: return { label: status, color: 'default' as const, icon: undefined };
    }
  };

  const steps = ['Enter Tax Figures', 'Review', 'Submit'];

  if (loading && !oauthStatus) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          <TaxIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          IRS Integration - IRS E-Filing
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Connect to IRS to submit Sales Tax returns and manage tax obligations
        </Typography>
      </Box>

      {/* Error Alert */}
      {loadError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Unable to load IRS status: {loadError}. Please ensure you are logged in.
        </Alert>
      )}

      {/* Connection Status */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Connection Status
        </Typography>
        
        {oauthStatus ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    OAuth Configuration
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {oauthStatus.is_configured ? (
                      <>
                        <CheckIcon color="success" />
                        <Typography variant="h6">Configured</Typography>
                      </>
                    ) : (
                      <>
                        <ErrorIcon color="error" />
                        <Typography variant="h6">Not Configured</Typography>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Authorization Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {oauthStatus.has_valid_token ? (
                      <>
                        <CheckIcon color="success" />
                        <Typography variant="h6">Connected</Typography>
                      </>
                    ) : (
                      <>
                        <ErrorIcon color="error" />
                        <Typography variant="h6">Not Connected</Typography>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Environment
                  </Typography>
                  <Typography variant="h6">
                    {oauthStatus.environment || 'Sandbox'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Alert severity="info">Loading connection status...</Alert>
        )}

        <Box mt={3} display="flex" gap={2}>
          {!oauthStatus?.has_valid_token ? (
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={handleConnect}
              disabled={loading || !oauthStatus?.is_configured}
            >
              Connect to IRS
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshToken}
              disabled={loading}
            >
              Refresh Token
            </Button>
          )}
        </Box>
      </Paper>

      {/* VAT Obligations */}
      {oauthStatus?.has_valid_token && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Tax Obligations
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadObligations}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {obligations.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Received Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {obligations.map((obligation) => {
                    const statusInfo = getObligationStatus(obligation.status);
                    return (
                      <TableRow key={obligation.period_key}>
                        <TableCell>
                          {new Date(obligation.start_date).toLocaleDateString()} - {new Date(obligation.end_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{new Date(obligation.due_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusInfo.label}
                            color={statusInfo.color}
                            size="small"
                            icon={statusInfo.icon}
                          />
                        </TableCell>
                        <TableCell>
                          {obligation.received_date ? new Date(obligation.received_date).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {obligation.status === 'O' && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<SendIcon />}
                              onClick={() => {
                                setSelectedObligation(obligation);
                                setShowSubmitDialog(true);
                              }}
                            >
                              Submit Return
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No tax obligations found</Alert>
          )}
        </Paper>
      )}

      {/* Submit Sales Tax Return Dialog */}
      <Dialog open={showSubmitDialog} onClose={() => setShowSubmitDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Submit Sales Tax Return
          {selectedObligation && (
            <Typography variant="body2" color="text.secondary">
              Period: {new Date(selectedObligation.start_date).toLocaleDateString()} - {new Date(selectedObligation.end_date).toLocaleDateString()}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Box 1: Sales tax due on sales</Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={salesTaxReturn.vat_due_sales || ''}
                  onChange={(e) => setVatReturn({ ...salesTaxReturn, vat_due_sales: parseFloat(e.target.value) })}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Box 2: Sales tax due on acquisitions</Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={salesTaxReturn.vat_due_acquisitions || ''}
                  onChange={(e) => setVatReturn({ ...salesTaxReturn, vat_due_acquisitions: parseFloat(e.target.value) })}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Box 4: Sales tax reclaimed</Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={salesTaxReturn.vat_reclaimed_curr_period || ''}
                  onChange={(e) => setVatReturn({ ...salesTaxReturn, vat_reclaimed_curr_period: parseFloat(e.target.value) })}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Box 6: Total sales (ex sales tax)</Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={salesTaxReturn.total_value_sales_ex_vat || ''}
                  onChange={(e) => setVatReturn({ ...salesTaxReturn, total_value_sales_ex_vat: parseFloat(e.target.value) })}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Box 7: Total purchases (ex sales tax)</Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={salesTaxReturn.total_value_purchases_ex_vat || ''}
                  onChange={(e) => setVatReturn({ ...salesTaxReturn, total_value_purchases_ex_vat: parseFloat(e.target.value) })}
                  placeholder="0.00"
                />
              </Grid>
            </Grid>
          )}

          {activeStep === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please review your Sales Tax return before submitting to IRS
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Sales tax due on sales:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">${(salesTaxReturn.vat_due_sales || 0).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Sales tax reclaimed:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">${(salesTaxReturn.vat_reclaimed_curr_period || 0).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" fontWeight="bold">Net sales tax due:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6" color="primary">
                    ${(((salesTaxReturn.vat_due_sales || 0) + (salesTaxReturn.vat_due_acquisitions || 0)) - (salesTaxReturn.vat_reclaimed_curr_period || 0)).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={() => setActiveStep(activeStep - 1)}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={() => setActiveStep(activeStep + 1)}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleSubmitSalesTaxReturn}
              disabled={loading}
            >
              Submit to IRS
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default IRSIntegration;
